import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { ActionLog } from '@models/actions/action-log.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { getOne, StateDictionary } from '../helpers/store-base';
import { ActionLogsService } from './action-logs.service';

type ActionLogsState = {
  selectedActionLog: ActionLog | null;
  isLoading: boolean;
  selectedActionLogLoading: boolean;
};

const initialState: ActionLogsState = {
  selectedActionLog: null,
  isLoading: false,
  selectedActionLogLoading: false,
};

const actionLogsDictionary: StateDictionary<ActionLog> = {
  entityList: '', // Not used for single entity operations
  listLoading: 'isLoading',
  selectedEntity: 'selectedActionLog',
  selectedEntityLoading: 'selectedActionLogLoading',
  entityId: 'id',
  createLoading: '', // Not applicable
};

export const ActionLogsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('action-logs-store'),

  withComputed(store => ({
    selectedActionLogId: computed(() => store.selectedActionLog()?.id || null),
  })),

  withMethods((store, actionLogsService = inject(ActionLogsService), router = inject(Router)) => {
    const repositoryMethods = {
      getActionLogById: getOne<ActionLog>({
        store,
        service: actionLogsService,
        dictionary: actionLogsDictionary,
      }),
    };

    const navigationMethods = {
      goToActionLogsList: async (): Promise<boolean> => {
        const result = await router.navigate(['/', 'actions', 'logs']);
        if (!result) return false;
        patchState(store, { selectedActionLog: null });

        return true;
      },

      goToActionLogActions: async (actionId: string): Promise<boolean> => {
        const result = await router.navigate(['/', 'actions', 'logs', actionId]);
        if (!result) return false;

        if (!store.selectedActionLogLoading()) repositoryMethods.getActionLogById(actionId);
        return true;
      },
    };

    return {
      ...repositoryMethods,
      ...navigationMethods,

      resetState: (state: Partial<ActionLogsState>) => patchState(store, state),
    };
  }),
);

export interface ActionLogsStoreType {
  readonly selectedActionLog: Signal<ActionLog | null>;
  readonly isLoading: Signal<boolean>;
  readonly selectedActionLogLoading: Signal<boolean>;

  readonly selectedActionLogId: Signal<string | null>;

  readonly getActionLogById: (id: string) => void;
  readonly goToActionLogsList: () => Promise<boolean>;
  readonly goToActionLogActions: (actionId: string) => Promise<boolean>;
  readonly resetState: (state: Partial<ActionLogsState>) => void;
}
